<template>
  <div class="ai-report-container">
    <!-- 动态背景元素 -->
    <div class="bg-effects">
      <div class="floating-orb orb-1"></div>
      <div class="floating-orb orb-2"></div>
      <div class="floating-orb orb-3"></div>
      <div class="pulse-ring"></div>
    </div>

    <!-- 主内容区域 -->
    <div class="main-content">
      <!-- 头部 - 科幻风格 -->
      <div class="report-header">
        <div class="header-left">
          <h1 id="pageTitle">{{ companyName || '加载中···' }}</h1>
          <div class="portrait-selector-wrapper">
            <label class="selector-label">切换公司：</label>
            <el-select 
              v-model="selectedPortraitId" 
              placeholder="请选择公司画像" 
              filterable
              :loading="portraitListLoading"
              @change="onPortraitChange"
              class="portrait-select"
              size="default"
            >
              <el-option
                v-for="item in portraitList"
                :key="item.id"
                :label="`${item.company_name}`"
                :value="item.id"
              />
            </el-select>
          </div>
        </div>
        <div class="header-right">
          <div class="last-update">
            <Icon icon="mdi:clock-outline" />
            最后更新: <span id="lastUpdateTime">{{ lastUpdateTime }}</span>
          </div>
          <button class="copy-link-btn" @click="copyReportLink">
            <Icon icon="mdi:link-variant" />
            复制链接
          </button>
          <button class="refresh-btn" @click="loadAllData">
            <Icon icon="mdi:refresh" />
            刷新
          </button>
        </div>
      </div>

      <!-- 顶部行容器 -->
      <div class="top-row">
        <!-- 左侧面板 -->
        <div class="left-panel">
          <!-- 公司名片 -->
          <div class="card company-card">
            <div class="avatar">
              <Icon icon="mdi:chart-line" />
            </div>
            <div class="company-info">
              <h3 id="companyName">{{ companyName }}</h3>
              <p><Icon icon="mdi:compass" /> 方向: <span id="creationDirection">{{ creationDirection }}</span></p>
              <p><Icon icon="mdi:account-group" /> 人数: <span id="companySize">{{ companySize }}</span></p>
              <p><Icon icon="mdi:map-marker" /> 地址: <span id="companyAddress">{{ companyAddress }}</span></p>
            </div>
          </div>

          <!-- 文章数量统计 -->
          <div class="card">
            <div class="title"><Icon icon="mdi:chart-bar" /> 文章数量统计</div>
            <div class="stat-item">
              <span><Icon icon="mdi:calendar-month" /> 近一个月</span>
              <span id="lastMonthArticles">{{ lastMonthArticles }}篇</span>
            </div>
            <div class="stat-item">
              <span><Icon icon="mdi:chart-line" /> 近三个月</span>
              <span id="last3MonthsArticles">{{ last3MonthsArticles }}篇</span>
            </div>
            <div class="stat-item">
              <span><Icon icon="mdi:chart-bar" /> 近一年</span>
              <span id="lastYearArticles">{{ lastYearArticles }}篇</span>
            </div>
          </div>
        </div>

        <!-- 右侧面板 -->
        <div class="right-panel">
          <!-- 数据概览卡片 -->
          <div class="overview-cards">
            <div class="overview-card">
              <div class="icon"><Icon icon="mdi:key" /></div>
              <div class="label">核心关键词</div>
              <div class="value" id="coreKeywords">{{ coreKeywordsCount }}</div>
            </div>
            <div class="overview-card">
              <div class="icon"><Icon icon="mdi:fire" /></div>
              <div class="label">蒸馏关键词</div>
              <div class="value" id="distilledKeywords">{{ distilledKeywordsCount }}</div>
            </div>
            <div class="overview-card">
              <div class="icon"><Icon icon="mdi:tag" /></div>
              <div class="label">品牌关键词</div>
              <div class="value" id="brandKeywords">{{ brandKeywordsCount }}</div>
            </div>
            <div class="overview-card">
              <div class="icon"><Icon icon="mdi:file-document" /></div>
              <div class="label">总收录条数</div>
              <div class="value" id="totalCollections">{{ totalCollectionsCount }}</div>
            </div>
          </div>

          <!-- 图表与排名 -->
          <div class="charts-section">
            <div class="card pie-chart-card">
              <div class="title"><Icon icon="mdi:chart-pie" /> 各平台收录占比</div>
              <div class="pie-chart-container">
                <div ref="pieChart" class="chart" style="width: 100%; height: 100%;"></div>
              </div>
            </div>
            <div class="card">
              <div class="title"><Icon icon="mdi:trophy" /> 蒸馏关键词排名</div>
              <ul class="ranking-list" id="rankingList">
                <li v-for="(item, index) in keywordRanking" :key="index" class="ranking-item">
                  <span>{{ item.keyword }}</span>
                  <div class="progress-bar">
                    <div class="progress" :style="{ width: item.percentage + '%' }"></div>
                  </div>
                  <span>{{ item.count }}</span>
                </li>
                <li v-if="keywordRanking.length === 0" class="ranking-item">
                  <span style="color: #a0a8b8;">暂无排名数据</span>
                  <div class="progress-bar"><div class="progress" style="width: 0%"></div></div>
                  <span>0</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- 搜索与表格区域 -->
      <div class="search-section">
        <div class="search-tabs">
          <button class="tab" :class="{ active: currentTab === 'search' }" @click="changeTab('search')">
            <Icon icon="mdi:magnify" /> 关键词搜索
          </button>
          <button class="tab" :class="{ active: currentTab === 'brand' }" @click="changeTab('brand')">
            <Icon icon="mdi:tag" /> 品牌搜索
          </button>
          <button class="tab" :class="{ active: currentTab === 'qa' }" @click="changeTab('qa')">
            <Icon icon="mdi:comment-question" /> 问答搜索
          </button>
        </div>

        <div class="filter-bar">
          <div class="platform-tabs" ref="platformTabsRef">
            <span 
              v-for="platform in platformFilters" 
              :key="platform.value"
              class="platform-tab" 
              :class="{ active: currentPlatform === platform.value }"
              @click="filterByPlatform(platform.value)"
            >
              <Icon :icon="platform.icon" /> {{ platform.label }} (<span :id="`count${platform.label}`">{{ platform.count || 0 }}</span>)
            </span>
            <div class="swipe-hint" v-if="showSwipeHint">
              <Icon icon="mdi:chevron-right" /> 滑动查看更多
            </div>
          </div>
          <div class="device-tabs">
            <span class="device-tab" :class="{ active: currentDevice === 'mobile' }" @click="filterByDevice('mobile')">
              <Icon icon="mdi:cellphone" /> 移动端
            </span>
            <span class="device-tab" :class="{ active: currentDevice === 'pc' }" @click="filterByDevice('pc')">
              <Icon icon="mdi:desktop-classic" /> PC端
            </span>
          </div>
        </div>

        <select class="data-select" v-model="currentKeyword" @change="filterByKeyword">
          <option value="all">全部关键词</option>
          <option v-for="item in keywordOptions" :key="item" :value="item">{{ item }}</option>
        </select>

        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>核心关键词</th>
                <th>蒸馏关键词</th>
                <th>平台</th>
                <th>来源</th>
                <th>查询时间</th>
                <!-- <th>操作</th> -->
              </tr>
            </thead>
            <tbody id="tableBody">
              <tr v-if="tableDataLoading">
                <td colspan="6" style="text-align: center; padding: 2rem;">
                  <div class="loading-spinner-small"></div>
                  <span>加载数据中...</span>
                 </td>
               </tr>
              <tr v-else-if="tableData.length === 0">
                <td colspan="6" style="text-align: center; padding: 2rem; color: #a0a8b8;">
                  <Icon icon="mdi:database" style="font-size: 2rem; opacity: 0.5;" />
                  <span>暂无收录数据</span>
                 </td>
               </tr>
              <tr v-for="(item, index) in paginatedTableData" :key="index">
                <td :title="item.original_keyword">{{ item.original_keyword }}</td>
                <td :title="item.distilled_keyword">{{ item.distilled_keyword || '—' }}</td>
                <td>{{ item.platform }}</td>
                <td>{{ item.source_device }}</td>
                <td>{{ item.query_time }}</td>
                <!-- <td>
                  <span v-if="item.has_screenshot" class="action" @click="showScreenshot(index)">
                    <Icon icon="mdi:camera" /> 截图
                  </span>
                  <span v-else style="color: #a0a8b8; font-size: 0.85rem;">
                    <Icon icon="mdi:image-off" /> 无截图
                  </span>
                </td> -->
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页组件 -->
        <div class="pagination" v-if="totalTableItems > itemsPerPage">
          <button class="page-btn" @click="changePage(currentPage - 1)" :disabled="currentPage === 1">
            <Icon icon="mdi:chevron-left" /> 上一页
          </button>
          <div class="page-info">第 {{ currentPage }} 页 / 共 {{ totalPages }} 页 ({{ totalTableItems }} 条记录)</div>
          <button class="page-btn" @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">
            下一页 <Icon icon="mdi:chevron-right" />
          </button>
        </div>
      </div>
    </div>

    <!-- 底部装饰 -->
    <div class="footer-decoration">
      <div class="scan-line"></div>
      <div class="data-stream">
        <span v-for="n in 20" :key="n" class="data-bit">0101</span>
      </div>
    </div>

    <!-- 截图弹窗 -->
    <div class="modal-overlay" :class="{ active: modalVisible }" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <div class="modal-title"><Icon icon="mdi:camera" /> 收录查询截图</div>
          <div class="modal-close" @click="closeModal"><Icon icon="mdi:close" /></div>
        </div>
        <div class="modal-body">
          <div class="loading-spinner" v-if="screenshotLoading">
            <div class="spinner"></div>
            <div class="loading-text">加载截图中...</div>
          </div>
          <div v-else class="no-screenshot">
            <Icon icon="mdi:camera-off" class="no-screenshot-icon" />
            <div class="no-screenshot-text">
              截图功能演示<br>
              <small style="opacity: 0.7;">实际应用中这里会显示真实的收录截图</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, computed, nextTick, onBeforeUnmount } from 'vue'
import * as echarts from 'echarts'
import { ElMessage } from 'element-plus'
import { Icon } from '@iconify/vue'
import axios from 'axios'
import config from '@/config.js'
import Cookies from 'js-cookie'

export default {
  name: 'AiReport',
  components: { Icon },
  setup() {
    // ==================== 状态定义 ====================
    const portraitList = ref([])
    const portraitListLoading = ref(false)
    const selectedPortraitId = ref(null)
    
    // 用户信息 - 商户端从 store_user_info 获取
    const userInfo = computed(() => {
      return JSON.parse(Cookies.get('store_user_info') || '{}')
    })
    const userId = computed(() => userInfo.value.id)
    const storeId = computed(() => userInfo.value.store_id)
    
    // 基础信息
    const companyName = ref('加载中...')
    const creationDirection = ref('加载中...')
    const companySize = ref('加载中...')
    const companyAddress = ref('加载中...')
    const lastUpdateTime = ref('加载中...')
    
    // 文章统计
    const lastMonthArticles = ref(0)
    const last3MonthsArticles = ref(0)
    const lastYearArticles = ref(0)
    
    // 关键词统计
    const coreKeywordsCount = ref(0)
    const distilledKeywordsCount = ref(0)
    const brandKeywordsCount = ref(0)
    const totalCollectionsCount = ref(0)
    
    // 平台统计
    const platformStats = ref([])
    const platformTotal = ref(0)
    const platformFilters = ref([])
    
    // 排名
    const keywordRanking = ref([])
    
    // 表格数据
    const allTableData = ref([])
    const tableDataLoading = ref(false)
    
    // 筛选条件
    const currentTab = ref('search')
    const currentPlatform = ref('all')
    const currentDevice = ref('pc')
    const currentKeyword = ref('all')
    const keywordOptions = ref([])
    
    // 分页
    const currentPage = ref(1)
    const itemsPerPage = 20
    
    // 弹窗
    const modalVisible = ref(false)
    const screenshotLoading = ref(true)
    
    // 图表实例
    const pieChart = ref(null)
    let pieChartInstance = null
    
    // DOM 引用
    const platformTabsRef = ref(null)
    const showSwipeHint = ref(false)
    
    // 计算属性
    const tableData = computed(() => {
      let data = [...allTableData.value]
      data = data.filter(d => d.keyword_type === currentTab.value)
      
      if (currentPlatform.value !== 'all') {
        data = data.filter(item => item.platform === currentPlatform.value)
      }
      
      const deviceText = currentDevice.value === 'pc' ? 'PC端' : '移动端'
      data = data.filter(item => item.source_device === deviceText)
      
      if (currentKeyword.value !== 'all') {
        data = data.filter(item => 
          item.original_keyword.includes(currentKeyword.value) || 
          currentKeyword.value.includes(item.original_keyword)
        )
      }
      
      return data
    })
    
    const totalTableItems = computed(() => tableData.value.length)
    const totalPages = computed(() => Math.ceil(totalTableItems.value / itemsPerPage))
    const paginatedTableData = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage
      const end = start + itemsPerPage
      return tableData.value.slice(start, end)
    })
    
    // ==================== 方法 ====================
    // 获取画像列表 - 商户端
    const fetchPortraitList = async () => {
      portraitListLoading.value = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/store/api/portrait_management.php`,
          {
            action: 'list',
            store_id: storeId.value,
            user_id: userId.value,
            page: 1,
            page_size: 100
          }
        )
        
        if (response.data.data) {
          portraitList.value = response.data.data
          if (portraitList.value.length > 0 && !selectedPortraitId.value) {
            selectedPortraitId.value = portraitList.value[0].id
            fetchReportData()
          }
        }
      } catch (error) {
        console.error('获取画像列表失败:', error)
        ElMessage.error('获取画像列表失败')
      } finally {
        portraitListLoading.value = false
      }
    }
    
    // 获取报表数据 - 使用 report_v2_api.php
    const fetchReportData = async () => {
      if (!selectedPortraitId.value) return
      
      tableDataLoading.value = true
      try {
        const response = await axios.get(`${config.BASE_URL}/store/api/report_v2_api.php?id=${selectedPortraitId.value}`)
        const data = response.data
        
        if (data.error) {
          ElMessage.error(data.error)
          return
        }
        
        if (data.success) {
          const baseInfo = data.base_info
          companyName.value = baseInfo.company_name
          creationDirection.value = baseInfo.creation_direction
          companySize.value = baseInfo.company_size
          companyAddress.value = baseInfo.regions
          lastUpdateTime.value = data.last_update
          
          const articleStats = data.article_stats
          lastMonthArticles.value = articleStats.last_month
          last3MonthsArticles.value = articleStats.last_3_months
          lastYearArticles.value = articleStats.last_year
          
          const keywordStats = data.keyword_stats
          coreKeywordsCount.value = keywordStats.core_keywords
          distilledKeywordsCount.value = keywordStats.distilled_keywords
          brandKeywordsCount.value = keywordStats.brand_keywords
          totalCollectionsCount.value = keywordStats.total_collections
          
          platformStats.value = data.platform_stats.platforms || []
          platformTotal.value = data.platform_stats.total || 0
          
          const counts = data.platform_counts
          platformFilters.value = [
            { value: 'all', label: '全部', icon: 'mdi:earth', count: counts.all },
            { value: '豆包', label: '豆包', icon: 'mdi:music', count: counts['豆包'] || 0 },
            { value: '文心一言', label: '文心一言', icon: 'mdi:robot', count: counts['文心一言'] || 0 },
            { value: 'DeepSeek', label: 'DeepSeek', icon: 'mdi:rocket', count: counts['DeepSeek'] || 0 },
            { value: 'Kimi', label: 'Kimi', icon: 'mdi:snowflake', count: counts['Kimi'] || 0 },
            { value: '腾讯元宝', label: '腾讯元宝', icon: 'mdi:gem', count: counts['腾讯元宝'] || 0 },
            { value: '通义千问', label: '通义千问', icon: 'mdi:star', count: counts['通义千问'] || 0 }
          ]
          
          const ranking = data.keyword_ranking || []
          const maxCount = ranking.length > 0 ? Math.max(...ranking.map(r => r.count)) : 0
          keywordRanking.value = ranking.map(item => ({
            keyword: item.keyword,
            count: item.count.toLocaleString(),
            percentage: maxCount > 0 ? Math.round((item.count / maxCount) * 100) : 0
          }))
          
          keywordOptions.value = ranking.map(item => item.keyword)
          allTableData.value = data.table_data || []
          
          await nextTick()
          initPieChart()
          checkScrollHint()
        }
      } catch (error) {
        console.error('获取报表数据失败:', error)
        ElMessage.error('获取报表数据失败')
      } finally {
        tableDataLoading.value = false
      }
    }
    
    // 初始化饼图（使用 ECharts）
    const initPieChart = () => {
      if (!pieChart.value || platformStats.value.length === 0) return
      
      if (pieChartInstance) {
        pieChartInstance.dispose()
      }
      
      const pieData = platformStats.value.map(p => ({
        name: p.name,
        value: p.count
      }))
      
      const colors = ['#6E21FE', '#8B5CF6', '#A78BFA', '#9B6EFF', '#886AFF', '#B48CFF']
      
      pieChartInstance = echarts.init(pieChart.value)
      pieChartInstance.setOption({
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(13, 5, 26, 0.9)',
          borderColor: '#6E21FE',
          borderWidth: 1,
          textStyle: { color: '#E0E6FF' },
          formatter: '{b}: {d}% ({c}条)'
        },
        series: [{
          name: '平台占比',
          type: 'pie',
          radius: ['60%', '80%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: '#0D051A',
            borderWidth: 2
          },
          label: { show: false },
          emphasis: {
            scale: true,
            label: {
              show: true,
              formatter: '{b}: {d}%',
              color: '#E0E6FF'
            }
          },
          data: pieData,
          color: colors
        }],
        graphic: [{
          type: 'text',
          left: 'center',
          top: 'center',
          style: {
            text: platformTotal.value.toLocaleString(),
            fill: '#E0E6FF',
            fontSize: 24,
            fontWeight: 'bold'
          },
          z: 100
        }]
      })
    }
    
    // 切换画像
    const onPortraitChange = (id) => {
      selectedPortraitId.value = id
      currentPage.value = 1
      currentPlatform.value = 'all'
      currentDevice.value = 'pc'
      currentKeyword.value = 'all'
      fetchReportData()
    }
    
    // 加载所有数据
    const loadAllData = () => {
      fetchReportData()
    }
    
    // 复制链接 - 商户端使用 /store/api/report.html
    const copyReportLink = async () => {
      if (!selectedPortraitId.value) {
        ElMessage.warning('请先选择公司')
        return
      }
      const link = `${window.location.origin}/store/api/report.html?id=${selectedPortraitId.value}`
      try {
        await navigator.clipboard.writeText(link)
        ElMessage.success('链接已复制到剪贴板')
      } catch (err) {
        ElMessage.error('复制失败，请手动复制')
      }
    }
    
    // 切换标签
    const changeTab = (tab) => {
      currentTab.value = tab
      currentPage.value = 1
    }
    
    // 按平台筛选
    const filterByPlatform = (platform) => {
      currentPlatform.value = platform
      currentPage.value = 1
    }
    
    // 按设备筛选
    const filterByDevice = (device) => {
      currentDevice.value = device
      currentPage.value = 1
    }
    
    // 按关键词筛选
    const filterByKeyword = () => {
      currentPage.value = 1
    }
    
    // 切换页码
    const changePage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page
      }
    }
    
    // 显示截图
    const showScreenshot = () => {
      modalVisible.value = true
      screenshotLoading.value = true
      setTimeout(() => {
        screenshotLoading.value = false
      }, 1000)
    }
    
    // 关闭弹窗
    const closeModal = () => {
      modalVisible.value = false
      screenshotLoading.value = true
    }
    
    // 检查滑动提示
    const checkScrollHint = () => {
      setTimeout(() => {
        if (platformTabsRef.value) {
          showSwipeHint.value = platformTabsRef.value.scrollWidth > platformTabsRef.value.clientWidth
        }
      }, 100)
    }
    
    // 导出报表
    const exportReport = () => {
      ElMessage.success('报表导出功能开发中...')
    }
    
    const handleResize = () => {
      if (pieChartInstance) {
        pieChartInstance.resize()
      }
      checkScrollHint()
    }
    
    // ==================== 生命周期 ====================
    onMounted(() => {
      fetchPortraitList()
      window.addEventListener('resize', handleResize)
    })
    
    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
      if (pieChartInstance) {
        pieChartInstance.dispose()
      }
    })
    
    return {
      portraitList,
      portraitListLoading,
      selectedPortraitId,
      companyName,
      creationDirection,
      companySize,
      companyAddress,
      lastUpdateTime,
      lastMonthArticles,
      last3MonthsArticles,
      lastYearArticles,
      coreKeywordsCount,
      distilledKeywordsCount,
      brandKeywordsCount,
      totalCollectionsCount,
      platformStats,
      platformTotal,
      platformFilters,
      keywordRanking,
      tableData,
      allTableData,
      tableDataLoading,
      paginatedTableData,
      totalTableItems,
      totalPages,
      currentPage,
      itemsPerPage,
      currentTab,
      currentPlatform,
      currentDevice,
      currentKeyword,
      keywordOptions,
      modalVisible,
      screenshotLoading,
      showSwipeHint,
      pieChart,
      platformTabsRef,
      onPortraitChange,
      loadAllData,
      copyReportLink,
      changeTab,
      filterByPlatform,
      filterByDevice,
      filterByKeyword,
      changePage,
      showScreenshot,
      closeModal,
      exportReport
    }
  }
}
</script>

<style scoped lang="scss">
.ai-report-container {
  padding: 24px;
  min-height: calc(100vh - 70px);
  background: linear-gradient(135deg, #0D051A 0%, #1A0B2E 50%, #2D1B47 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(2px 2px at 20px 30px, #6E21FE, transparent),
      radial-gradient(2px 2px at 40px 70px, #FF7048, transparent),
      radial-gradient(1px 1px at 90px 40px, #00F2FE, transparent),
      radial-gradient(1px 1px at 130px 80px, #6E21FE, transparent),
      radial-gradient(2px 2px at 160px 30px, #FF7048, transparent);
    background-repeat: repeat;
    animation: twinkle 4s ease-in-out infinite alternate;
    pointer-events: none;
  }
}

@keyframes twinkle {
  0% { opacity: 0.3; transform: translateY(0px); }
  100% { opacity: 0.8; transform: translateY(-10px); }
}

.bg-effects {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.floating-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  animation: float 6s ease-in-out infinite;

  &.orb-1 {
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, #6E21FE 0%, transparent 70%);
    top: 10%;
    left: 10%;
    animation-delay: 0s;
  }
  &.orb-2 {
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, #FF7048 0%, transparent 70%);
    top: 60%;
    right: 10%;
    animation-delay: 2s;
  }
  &.orb-3 {
    width: 100px;
    height: 100px;
    background: radial-gradient(circle, #00F2FE 0%, transparent 70%);
    bottom: 20%;
    left: 20%;
    animation-delay: 4s;
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0px) scale(1); }
  50% { transform: translateY(-20px) scale(1.1); }
}

.pulse-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 500px;
  height: 500px;
  border: 2px solid rgba(110, 33, 254, 0.3);
  border-radius: 50%;
  animation: pulse 3s ease-out infinite;
}

@keyframes pulse {
  0% { transform: translate(-50%, -50%) scale(0.8); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(1.2); opacity: 0; }
}

.main-content {
  position: relative;
  z-index: 1;
  background: rgba(13, 5, 26, 0.7);
  border-radius: 24px;
  padding: 32px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(110, 33, 254, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

/* 头部样式 */
.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(110, 33, 254, 0.3);
  flex-wrap: wrap;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.report-header h1 {
  font-size: 1.8rem;
  font-weight: 700;
  background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 50%, #a78bfa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
}

.portrait-selector-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.selector-label {
  color: #a0a8b8;
  font-size: 0.9rem;
}

.portrait-select {
  width: 220px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.last-update {
  font-size: 0.8rem;
  color: #a0a8b8;
  display: flex;
  align-items: center;
  gap: 6px;
}

.copy-link-btn, .refresh-btn {
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #e0e6ff;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #6e21fe;
    transform: translateY(-1px);
  }
}

/* 顶部行 */
.top-row {
  display: grid;
  grid-template-columns: 1fr 3fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

/* 卡片样式 */
.card {
  background: rgba(13, 5, 26, 0.7);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(110, 33, 254, 0.4);
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.4s ease;
}

.card:hover {
  transform: translateY(-4px);
  border-color: rgba(110, 33, 254, 0.6);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.card .title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1.2rem;
  color: #e0e6ff;
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid rgba(110, 33, 254, 0.3);
}

/* 公司名片 */
.company-card {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: linear-gradient(135deg, rgba(110, 33, 254, 0.1), rgba(139, 92, 246, 0.05));
}

.avatar {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  box-shadow: 0 8px 25px rgba(110, 33, 254, 0.4);
}

.company-info h3 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #e0e6ff;
  margin-bottom: 0.5rem;
}

.company-info p {
  font-size: 0.9rem;
  margin: 0.3rem 0;
  color: #a0a8b8;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 统计项 */
.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.stat-item span:first-child {
  color: #a0a8b8;
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-item span:last-child {
  color: #8b5cf6;
  font-weight: 700;
}

/* 数据概览卡片 */
.overview-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.overview-card {
  background: rgba(13, 5, 26, 0.7);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(110, 33, 254, 0.4);
  border-radius: 16px;
  padding: 1.2rem;
  text-align: center;
  transition: all 0.4s ease;
}

.overview-card:hover {
  transform: translateY(-5px);
  border-color: rgba(110, 33, 254, 0.6);
}

.overview-card .icon {
  font-size: 1.8rem;
  margin-bottom: 0.8rem;
  color: #8b5cf6;
}

.overview-card .label {
  font-size: 0.85rem;
  color: #a0a8b8;
  margin-bottom: 0.5rem;
}

.overview-card .value {
  font-size: 1.8rem;
  font-weight: 800;
  background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* 图表区域 */
.charts-section {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
}

.pie-chart-container {
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.chart {
  width: 100%;
  height: 100%;
}

/* 排名列表 */
.ranking-list {
  list-style: none;
}

.ranking-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem 1rem;
  margin: 0.3rem 0;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  border: 1px solid rgba(110, 33, 254, 0.2);
}

.ranking-item span:first-child {
  color: #e0e6ff;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.progress-bar {
  flex: 0 0 120px;
  height: 8px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  overflow: hidden;
  margin: 0 1rem;
}

.progress {
  height: 100%;
  background: linear-gradient(90deg, #6e21fe 0%, #8b5cf6 100%);
  border-radius: 4px;
  transition: width 0.5s ease;
}

.ranking-item span:last-child {
  color: #8b5cf6;
  font-weight: 700;
  min-width: 60px;
  text-align: right;
}

/* 搜索区域 */
.search-section {
  background: rgba(13, 5, 26, 0.7);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(110, 33, 254, 0.4);
  border-radius: 20px;
  padding: 1.8rem;
  margin-top: 1.5rem;
}

.search-tabs {
  display: flex;
  gap: 0.8rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.tab {
  padding: 0.8rem 1.5rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(110, 33, 254, 0.3);
  cursor: pointer;
  color: #a0a8b8;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.tab:hover {
  background: rgba(110, 33, 254, 0.15);
  color: #e0e6ff;
}

.tab.active {
  background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 100%);
  color: #fff;
  border: none;
}

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
}

.platform-tabs {
  display: flex;
  gap: 0.8rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  flex: 1;
  min-width: 0;
}

.device-tabs {
  display: flex;
  gap: 0.8rem;
  flex-shrink: 0;
}

.platform-tab, .device-tab {
  font-size: 0.85rem;
  color: #a0a8b8;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.platform-tab:hover, .device-tab:hover {
  background: rgba(110, 33, 254, 0.1);
  color: #e0e6ff;
}

.platform-tab.active, .device-tab.active {
  background: rgba(110, 33, 254, 0.2);
  color: #8b5cf6;
  border: 1px solid rgba(110, 33, 254, 0.4);
}

.swipe-hint {
  display: none;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #8b5cf6;
  background: rgba(110, 33, 254, 0.15);
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
}

@media (max-width: 768px) {
  .swipe-hint { display: flex; }
  .filter-bar { flex-direction: column; align-items: stretch; }
  .platform-tabs { width: 100%; }
  .device-tabs { justify-content: flex-end; }
}

.data-select {
  width: 100%;
  padding: 0.8rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(110, 33, 254, 0.4);
  margin-bottom: 1.5rem;
  background: rgba(13, 5, 26, 0.8);
  color: #e0e6ff;
}

/* 表格 */
.table-container {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid rgba(110, 33, 254, 0.2);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  min-width: 800px;
}

.data-table th {
  background: linear-gradient(135deg, rgba(110, 33, 254, 0.2), rgba(139, 92, 246, 0.1));
  color: #8b5cf6;
  font-weight: 600;
  padding: 1rem;
  text-align: left;
  border-bottom: 2px solid rgba(110, 33, 254, 0.3);
}

.data-table td {
  color: #a0a8b8;
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.data-table tr:hover td {
  background: rgba(110, 33, 254, 0.05);
  color: #e0e6ff;
}

.action {
  color: #8b5cf6;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.action:hover {
  color: #6e21fe;
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(110, 33, 254, 0.2);
}

.page-btn {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  background: rgba(110, 33, 254, 0.1);
  border: 1px solid rgba(110, 33, 254, 0.3);
  color: #a0a8b8;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.page-btn:hover:not(:disabled) {
  background: rgba(110, 33, 254, 0.2);
  color: #e0e6ff;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  color: #a0a8b8;
  font-size: 0.9rem;
  margin: 0 1rem;
}

/* 弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(13, 5, 26, 0.95);
  backdrop-filter: blur(25px);
  display: none;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-overlay.active {
  display: flex;
}

.modal-content {
  background: rgba(13, 5, 26, 0.9);
  backdrop-filter: blur(30px);
  border: 2px solid rgba(110, 33, 254, 0.6);
  border-radius: 20px;
  padding: 2rem;
  max-width: 90%;
  max-height: 90%;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(110, 33, 254, 0.3);
}

.modal-title {
  font-size: 1.3rem;
  font-weight: 600;
  background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-close {
  background: rgba(110, 33, 254, 0.2);
  border: 1px solid rgba(110, 33, 254, 0.4);
  border-radius: 10px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba(110, 33, 254, 0.3);
  border-radius: 50%;
  border-top-color: #6e21fe;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

.loading-spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(110, 33, 254, 0.3);
  border-radius: 50%;
  border-top-color: #6e21fe;
  animation: spin 1s linear infinite;
  display: inline-block;
  margin-right: 10px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.no-screenshot {
  text-align: center;
  padding: 2rem;
}

.no-screenshot-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

/* 底部装饰 */
.footer-decoration {
  margin-top: 32px;
}

.scan-line {
  height: 2px;
  background: linear-gradient(90deg, transparent, #6e21fe, #ff7048, #00f2fe, transparent);
  animation: scan 3s linear infinite;
}

.data-stream {
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
}

.data-bit {
  color: rgba(160, 168, 184, 0.3);
  font-size: 10px;
  font-family: monospace;
  animation: dataFlow 2s linear infinite;
}

@keyframes scan {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

@keyframes dataFlow {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}

/* 响应式 */
@media (max-width: 1024px) {
  .top-row { grid-template-columns: 1fr; }
  .charts-section { grid-template-columns: 1fr; }
  .overview-cards { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 768px) {
  .ai-report-container { padding: 16px; }
  .main-content { padding: 20px; }
  .report-header { flex-direction: column; align-items: flex-start; }
  .header-left { flex-direction: column; align-items: flex-start; }
  .overview-cards { grid-template-columns: 1fr; }
}

@media (max-width: 480px) {
  .progress-bar { flex: 0 0 80px; }
  .platform-tab, .device-tab { padding: 0.3rem 0.8rem; font-size: 0.75rem; }
}
</style>

<style>
/* 全局表格样式覆盖 */
.ai-report-container .el-select-dropdown {
  background: rgba(13, 5, 26, 0.95) !important;
  border: 1px solid rgba(110, 33, 254, 0.3) !important;
}

.ai-report-container .el-select-dropdown__item {
  color: #e0e6ff !important;
}

.ai-report-container .el-select-dropdown__item:hover {
  background: rgba(110, 33, 254, 0.2) !important;
}

.ai-report-container .el-select-dropdown__item.selected {
  background: rgba(110, 33, 254, 0.3) !important;
  color: #8b5cf6 !important;
}

.ai-report-container .el-input__wrapper {
  background: rgba(13, 5, 26, 0.8) !important;
  border: 1px solid rgba(110, 33, 254, 0.3) !important;
}

.ai-report-container .el-input__inner {
  color: #e0e6ff !important;
}
</style>